/**
 * The app's state, as Svelte runes. Screens read from here and call do_() to change things.
 *
 *   confirmed   state folded from events the server has confirmed
 *   current     confirmed + this device's waiting changes (what you see)
 *   ledger      every computed number for `current` (or for the past moment you're viewing)
 *
 * Every change is checked with core.decide against what you see before it goes in the outbox,
 * so mistakes ("someone is already called Emily") show up instantly, even offline.
 */
import {
  apply,
  buildHistory,
  memberCanTick,
  computeLedger,
  decide,
  emptyState,
  stateAt,
  type Command,
  type GroupState,
  type HistoryEntry,
  type Ledger,
  type MemberAccess,
  type StoredEvent,
} from "@split/core";
import { ApiError, api } from "./api.ts";
import {
  getMeta,
  loadEvents,
  loadOutbox,
  openLocal,
  setMeta,
  type LocalDB,
  type OutboxEntry,
} from "./db.ts";
import { today } from "./format.ts";
import { foldEvents, type FoldCache } from "./fold.ts";
import { SyncEngine, type SyncStatus } from "./sync.ts";
import { people } from "./people.svelte.ts";
import * as T from "../settings/receipts.ts";

export interface Toast {
  id: number;
  text: string;
  kind: "info" | "error";
  action?: { label: string; run: () => void };
}

export interface GroupInfo {
  id: string;
  name: string;
}

/** A group shown the way one member sees it: the same screens, only their own data. */
export interface MemberSession {
  groupId: string;
  /** Whose view this is: the member's own person (or, in Member view, whoever you picked). */
  personId: string;
  role: "member" | "treasurer";
  access: Required<MemberAccess>;
  state: GroupState;
  updatedAt: string | null;
  /** "member" = a friend's own account; "preview" = you checking what they see. */
  mode: "member" | "preview";
}

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

class AppStore {
  ready = $state(false);
  signedIn = $state<boolean | null>(null);
  needsSetup = $state(false);
  userName = $state("");
  username = $state<string | null>(null);
  /** "owner" runs the ledger; "member" is a friend who sees only their own items. */
  role = $state<"owner" | "member" | null>(null);
  /** The treasurer's "Member view" switch: see the app the way friends' accounts do. */
  viewAsMember = $state(readFlag("viewAsMember"));
  groups = $state<GroupInfo[]>([]);
  groupId = $state<string | null>(null);

  events = $state.raw<StoredEvent[]>([]);
  outbox = $state.raw<OutboxEntry[]>([]);
  syncStatus = $state<SyncStatus>("idle");
  lastSyncedAt = $state<string | null>(null);
  nextRetryAt = $state<number | null>(null);
  online = $state(typeof navigator === "undefined" ? true : navigator.onLine);

  /** When set, the whole app shows the ledger as it was right after this event. */
  asOfSeq = $state<number | null>(null);
  toasts = $state<Toast[]>([]);
  today = $state(today());

  private db: LocalDB | null = null;
  private engine: SyncEngine | null = null;
  private confirmedCache: FoldCache | null = null;
  private toastId = 0;

  /** State from confirmed events, folded incrementally as new events arrive. */
  confirmed = $derived.by(() => {
    const next = foldEvents(this.confirmedCache, this.groupId ?? "none", this.events);
    this.confirmedCache = next;
    return next.state;
  });

  private byId = $derived(new Map(this.events.map((e) => [e.id, e])));

  /** Set while the screens show a group the way a member sees it (see openMemberGroup). */
  memberSession = $state.raw<MemberSession | null>(null);
  memberError = $state<string | null>(null);

  /** Confirmed state plus waiting changes from this device. */
  current = $derived.by(() => {
    if (this.memberSession) return this.memberSession.state;
    const pending = this.outbox.filter((e) => e.status === "pending");
    if (pending.length === 0) return this.confirmed;
    let state = structuredClone(this.confirmed);
    for (const p of pending) {
      const r = decide(state, p.cmd, { findEvent: (id) => this.byId.get(id) });
      if (r.ok) for (const e of r.value) state = apply(state, e);
    }
    return state;
  });

  /** What the screens show: now, or a moment in the past. */
  viewState = $derived.by(() =>
    this.asOfSeq === null
      ? this.current
      : stateAt(this.groupId ?? "none", this.events, this.asOfSeq),
  );

  ledger: Ledger = $derived(computeLedger(this.viewState, this.today));
  history: HistoryEntry[] = $derived(buildHistory(this.groupId ?? "none", this.events));
  pendingCount = $derived(this.outbox.filter((e) => e.status === "pending").length);
  failed = $derived(this.outbox.filter((e) => e.status === "failed"));
  readOnly = $derived(this.asOfSeq !== null);
  /** Whether the screens may change the group (members only ever mark their own items). */
  canEdit = $derived(this.asOfSeq === null && this.memberSession === null);
  /** Who "you" is on the screens: the member being shown, or the group's owner. */
  meId = $derived(this.memberSession?.personId ?? this.current.settings.ownerPersonId);
  /** Whether a tick for this person can be changed here. */
  canTick(personId: string, itemId?: string): boolean {
    if (this.asOfSeq !== null) return false;
    const m = this.memberSession;
    if (m === null) return true;
    if (personId !== m.personId) return false;
    // A plain member only while the receipt is open (see receiptLock.ts).
    return m.role === "treasurer" || itemId === undefined || memberCanTick(m.state, itemId);
  }

  /** Ids of items/people touched by changes still waiting to sync (shown with a dashed edge). */
  pendingTouches = $derived.by(() => {
    const items = new Set<string>();
    const people = new Set<string>();
    for (const p of this.outbox) {
      if (p.status !== "pending") continue;
      const c = p.cmd as Record<string, unknown>;
      if (typeof c.itemId === "string") items.add(c.itemId);
      if (typeof c.personId === "string") people.add(c.personId);
    }
    return { items, people };
  });

  // ---------------------------------------------------------------- startup

  async start(): Promise<void> {
    this.db = await openLocal();
    window.addEventListener("online", () => {
      this.online = true;
      this.engine?.kick();
    });
    window.addEventListener("offline", () => (this.online = false));
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        this.today = today();
        this.engine?.kick();
        this.refreshMemberGroup();
      }
    });
    setInterval(() => {
      if (document.visibilityState !== "visible" || this.asOfSeq !== null) return;
      this.engine?.kick();
      this.refreshMemberGroup();
      this.syncOthers();
    }, 30_000);

    // Open the last group straight from this device, before asking the network anything.
    const lastGroup = await getMeta<string>(this.db, "lastGroup");
    const cachedGroups = (await getMeta<GroupInfo[]>(this.db, "groups")) ?? [];
    this.groups = cachedGroups;
    const cachedUser = await getMeta<string>(this.db, "userName");
    const cachedRole = (await getMeta<"owner" | "member">(this.db, "role")) ?? null;
    if (cachedUser) {
      this.userName = cachedUser;
      this.signedIn = true;
      this.role = cachedRole ?? "owner";
    }
    if (this.role === "member") {
      // Members don't keep the full ledger on their device; their screens load their own view.
      this.ready = true;
      await this.refreshAccount();
    } else if (lastGroup) {
      // This device already has a group: show it immediately and check the server in the background.
      await this.openGroup(lastGroup);
      this.ready = true;
      await this.refreshAccount();
    } else {
      // First visit on this device: ask the server first, so "Start a group" never flashes
      // for someone who already has groups. Offline, this fails fast and we carry on.
      await this.refreshAccount();
      this.ready = true;
    }
  }

  /** Checks sign-in and the group list with the server (quietly does nothing when offline). */
  /** Bumped on sign-out, so an account check that started before it can't sign you back in. */
  private authGen = 0;

  async refreshAccount(): Promise<void> {
    const gen = this.authGen;
    try {
      const me = await api<{
        signedIn: true;
        user: { name: string; username: string | null; role: "owner" | "member" };
      }>("/api/me");
      if (gen !== this.authGen) return;
      this.signedIn = true;
      this.userName = me.user.name;
      this.username = me.user.username;
      this.role = me.user.role;
      await setMeta(this.db!, "userName", me.user.name);
      await setMeta(this.db!, "role", me.user.role);
      if (me.user.role === "member") {
        this.engine?.stop();
        this.engine = null;
        this.groupId = null;
        return;
      }
      const { groups } = await api<{ groups: GroupInfo[] }>("/api/groups");
      this.groups = groups;
      await setMeta(this.db!, "groups", $state.snapshot(groups));
      if (!this.groupId && groups[0]) await this.openGroup(groups[0].id);
      this.engine?.kick();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        const body = await fetch("/api/me")
          .then((r) => r.json())
          .catch(() => ({}));
        this.signedIn = false;
        this.role = null;
        this.needsSetup = Boolean((body as { needsSetup?: boolean }).needsSetup);
        await setMeta(this.db!, "userName", "");
      }
      // Offline: keep using what this device has.
    }
  }

  async openGroup(groupId: string): Promise<void> {
    this.engine?.stop();
    this.asOfSeq = null;
    // Never show the group you're opening with the last group's changes: start it empty and
    // fill it from this device straight away (a few milliseconds), then from the server.
    if (groupId !== this.groupId) {
      this.events = [];
      this.outbox = [];
    }
    this.groupId = groupId;
    await setMeta(this.db!, "lastGroup", groupId);
    const engine = new SyncEngine(this.db!, groupId, {
      onChange: () => this.copyFrom(engine),
      onRejected: (entry) =>
        this.toast(`Couldn't save: ${entry.error ?? "the server refused it"}`, "error"),
    });
    this.engine = engine;
    await engine.load();
    // A background catch-up for this group finishes first, so both never write it at once;
    // then read what it saved.
    const running = this.othersRunning.get(groupId);
    if (running) {
      await running;
      if (engine !== this.engine) return;
      await engine.load();
    }
    if (this.signedIn !== false) engine.kick();
  }

  private copyFrom(engine: SyncEngine) {
    if (engine !== this.engine) return;
    this.events = engine.events;
    this.outbox = engine.outbox;
    this.syncStatus = engine.status;
    this.lastSyncedAt = engine.lastSyncedAt;
    this.nextRetryAt = engine.nextRetryAt;
    if (engine.status === "signed-out") this.signedIn = false;
  }

  /**
   * A group's state as this device knows it (confirmed events plus waiting changes), for the
   * Overview. Null when this device has never opened the group.
   */
  /** Every confirmed change this device has for a group (for downloads). */
  async groupEvents(groupId: string): Promise<StoredEvent[]> {
    if (groupId === this.groupId) return $state.snapshot(this.events) as StoredEvent[];
    if (!this.db) return [];
    return loadEvents(this.db, groupId);
  }

  /**
   * Bumped whenever a group that isn't open gets new changes from the server, so screens that
   * add up every group (Overview, Money) re-read them.
   */
  othersVersion = $state(0);
  private othersSyncedAt = new Map<string, number>();
  private othersRunning = new Map<string, Promise<void>>();

  /**
   * Brings a group that isn't open up to date with the server in the background: sends any
   * changes still waiting for it on this device and pulls what others changed. The open group
   * already syncs on its own.
   */
  syncGroup(groupId: string, force = false): Promise<void> {
    if (!this.db || this.signedIn === false || this.role === "member") return Promise.resolve();
    if (groupId === this.groupId) {
      this.engine?.kick();
      return Promise.resolve();
    }
    const running = this.othersRunning.get(groupId);
    if (running) return running;
    if (!force && Date.now() - (this.othersSyncedAt.get(groupId) ?? 0) < 20_000)
      return Promise.resolve();
    this.othersSyncedAt.set(groupId, Date.now());
    const engine = new SyncEngine(this.db, groupId);
    const run = (async () => {
      await engine.load();
      const before = engine.cursor;
      await engine.syncOnce();
      engine.stop();
      if (engine.cursor !== before) this.othersVersion++;
    })()
      .catch(() => {
        /* offline: the saved copy is used */
      })
      .finally(() => this.othersRunning.delete(groupId));
    this.othersRunning.set(groupId, run);
    return run;
  }

  /** Every group you can see, brought up to date (throttled unless forced). */
  syncOthers(force = false) {
    for (const g of this.groups) if (g.id !== this.groupId) void this.syncGroup(g.id, force);
  }

  /** After a change made outside the ledger (tutoring pay, a member's tick): pull everywhere now. */
  syncEverything() {
    this.engine?.kick();
    this.syncOthers(true);
    // Tutoring changes don't move any group, but they move People's totals and the Overview's.
    people.refreshSoon();
  }

  async groupSnapshot(groupId: string): Promise<GroupState | null> {
    if (groupId === this.groupId) return this.current;
    if (!this.db) return null;
    void this.syncGroup(groupId);
    const [events, outbox] = await Promise.all([
      loadEvents(this.db, groupId),
      loadOutbox(this.db, groupId),
    ]);
    if (events.length === 0 && outbox.length === 0) return null;
    let state = emptyState(groupId);
    for (const e of events) state = apply(state, e);
    const byId = new Map(events.map((e) => [e.id, e]));
    for (const p of outbox) {
      if (p.status !== "pending") continue;
      const r = decide(state, p.cmd, { findEvent: (id) => byId.get(id) });
      if (r.ok) for (const e of r.value) state = apply(state, e);
    }
    return state;
  }

  /**
   * Makes a group and opens it. With `withMe`, you're in it straight away as yourself (your full
   * name, set as "you"), so you can tick your own items like everyone else.
   */
  async createGroup(name: string, withMe = false): Promise<void> {
    const g = await api<GroupInfo>("/api/groups", { body: { name } });
    this.groups = [...this.groups, g];
    await setMeta(this.db!, "groups", $state.snapshot(this.groups));
    await this.openGroup(g.id);
    const myName = this.userName.trim();
    if (withMe && myName && this.groupId === g.id && !this.current.settings.ownerPersonId) {
      const personId = `p-${crypto.randomUUID().slice(0, 8)}`;
      if (!(await this.do_({ type: "person.add", personId, name: myName, color: "Blue" })))
        await this.do_({ type: "settings.update", patch: { ownerPersonId: personId } });
    }
  }

  // ---------------------------------------------------------------- changes

  /**
   * Makes a change. Returns an error message (and changes nothing) if the change isn't
   * allowed; otherwise saves it locally and syncs in the background.
   */
  async do_(cmd: Command, label?: string): Promise<string | null> {
    if (this.memberSession) return this.memberDo(cmd, label);
    if (!this.engine) return "Open a group first";
    if (this.readOnly) return "You're viewing the past. Return to now to make changes.";
    const r = decide(this.current, cmd, { findEvent: (id) => this.byId.get(id) });
    if (!r.ok) {
      this.toast(r.error.message, "error");
      return r.error.message;
    }
    if (r.value.length === 0) return null; // nothing changed
    try {
      await this.engine.enqueue(cmd);
    } catch (e) {
      const msg = "Couldn't save on this device. Check that the browser allows site storage.";
      console.error(e);
      this.toast(msg, "error");
      return msg;
    }
    if (label) this.toast(label);
    return null;
  }

  /** A member can only mark which items were theirs; everything else is the treasurer's. */
  private async memberDo(cmd: Command, label?: string): Promise<string | null> {
    const m = this.memberSession!;
    if (cmd.type !== "share.set" || cmd.personId !== m.personId || m.mode !== "member") {
      const msg =
        m.mode === "preview"
          ? "This is Member view: switch back to make changes."
          : "Only the treasurer can change that. You can mark which items were yours.";
      this.toast(msg, "error");
      return msg;
    }
    if (m.role !== "treasurer" && !memberCanTick(m.state, cmd.itemId)) {
      this.toast(T.RECEIPT_LOCKED_TOAST, "error");
      return T.RECEIPT_LOCKED_TOAST;
    }
    const r = decide(m.state, cmd, { findEvent: () => undefined });
    if (!r.ok) {
      this.toast(r.error.message, "error");
      return r.error.message;
    }
    if (r.value.length === 0) return null; // already that way
    // Show it straight away. Any copy still loading from before this tick is out of date, and
    // no copy is loaded while ticks are still saving, so a quick second tick never blinks back.
    let state = structuredClone(m.state);
    for (const e of r.value) state = apply(state, e);
    this.memberSession = { ...m, state };
    this.memberGen++;
    this.memberSaving++;
    let failed: string | null = null;
    try {
      await api(`/api/member/groups/${encodeURIComponent(m.groupId)}/claim`, {
        body: { items: [{ itemId: cmd.itemId, ticked: cmd.ticked }] },
      });
    } catch (e) {
      failed = e instanceof ApiError ? e.message : "Couldn't save. Check your connection.";
    } finally {
      this.memberSaving--;
    }
    const now = this.memberSession;
    if (failed) {
      // Take back only this tick; other ticks made meanwhile stay.
      if (now && now.groupId === m.groupId) {
        const back = decide(
          now.state,
          { ...cmd, ticked: !cmd.ticked },
          { findEvent: () => undefined },
        );
        if (back.ok) {
          let st = structuredClone(now.state);
          for (const e of back.value) st = apply(st, e);
          this.memberSession = { ...now, state: st };
        }
      }
      this.toast(failed, "error");
    } else if (label) this.toast(label);
    if (this.memberSaving === 0 && now && now.groupId === m.groupId)
      void this.openMemberGroup(m.groupId, "member", null);
    return failed;
  }

  /** Member ticks still being saved; the group isn't reloaded until they're all in. */
  private memberSaving = 0;

  /**
   * Shows a group the way a member sees it, on the same screens: a friend's own account
   * ("member"), or you checking what someone sees ("preview", for yourself or anyone).
   * The last copy is kept on the device, so it opens instantly and works offline.
   */
  async openMemberGroup(
    groupId: string,
    mode: "member" | "preview",
    as?: string | null,
  ): Promise<void> {
    const gen = ++this.memberGen;
    const key = `${mode}:state:${groupId}:${as ?? ""}`;
    const cur = this.memberSession;
    if (!cur || cur.groupId !== groupId || cur.mode !== mode || (as && cur.personId !== as)) {
      try {
        const saved = localStorage.getItem(key);
        this.memberSession = saved ? (JSON.parse(saved) as MemberSession) : null;
      } catch {
        this.memberSession = null;
      }
    }
    const url =
      mode === "preview"
        ? `/api/preview/groups/${encodeURIComponent(groupId)}/state${as ? `?as=${encodeURIComponent(as)}` : ""}`
        : `/api/member/groups/${encodeURIComponent(groupId)}/state`;
    try {
      const r = await api<Omit<MemberSession, "groupId" | "mode">>(url);
      // Something else was opened, or a tick was made, meanwhile: this copy is out of date.
      if (gen !== this.memberGen || this.memberSaving > 0) return;
      const next: MemberSession = { ...r, groupId, mode };
      this.memberSession = next;
      this.memberError = null;
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* storage blocked: fine */
      }
    } catch (e) {
      if (gen !== this.memberGen) return;
      if (e instanceof ApiError) {
        this.memberError = e.message;
        this.memberSession = null;
      }
    }
  }

  private memberGen = 0;

  /** Loads the open member group again, so changes others made show up without a reload. */
  refreshMemberGroup() {
    const m = this.memberSession;
    if (m)
      void this.openMemberGroup(m.groupId, m.mode, m.mode === "preview" ? this.previewAs : null);
  }

  closeMemberGroup() {
    this.memberGen++;
    this.memberSession = null;
    this.memberError = null;
  }

  async importCommands(cmds: { cmdId: string; cmd: Command }[]): Promise<boolean> {
    try {
      await this.engine?.enqueueMany(cmds);
      return true;
    } catch (e) {
      console.error(e);
      this.toast("Couldn't save the import on this device.", "error");
      return false;
    }
  }

  async discard(cmdId: string) {
    await this.engine?.discard(cmdId);
  }

  syncNow() {
    this.engine?.kick();
  }

  async undo(event: StoredEvent): Promise<void> {
    await this.do_({ type: "undo", eventId: event.id }, "Undone");
  }

  // ---------------------------------------------------------------- toasts

  toast(text: string, kind: Toast["kind"] = "info", action?: Toast["action"]) {
    const id = ++this.toastId;
    this.toasts = [...this.toasts, { id, text, kind, action }];
    setTimeout(() => this.dismiss(id), kind === "error" ? 6000 : 3500);
  }

  dismiss(id: number) {
    this.toasts = this.toasts.filter((t) => t.id !== id);
  }

  /** In Member view, whose account you're looking through (null = your own person). */
  previewAs = $state<string | null>(null);

  /** Member view; pass someone to see exactly what their account shows. */
  setViewAsMember(on: boolean, as: string | null = null) {
    this.previewAs = on ? as : null;
    if (!on) this.closeMemberGroup();
    this.viewAsMember = on;
    try {
      if (on) localStorage.setItem("viewAsMember", "1");
      else localStorage.removeItem("viewAsMember");
    } catch {
      /* fine: it just won't be remembered */
    }
  }

  async signOut() {
    this.authGen++;
    await api("/api/auth/logout", { body: {} }).catch(() => {});
    await setMeta(this.db!, "userName", "");
    await setMeta(this.db!, "role", null);
    this.signedIn = false;
    this.role = null;
    this.setViewAsMember(false);
  }
}

export const app = new AppStore();
